import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

import { NodeSelection } from "@tiptap/pm/state";

import { cn } from "@/lib/utils";

import type { FC } from "react";
import type { CustomImageNodeViewProps } from "@/components/tiptap/extensions/custom-image/image-node";

/* eslint-disable react-hooks/exhaustive-deps */

type Pixel = `${number}px`;

type PixelAttribute<TDefault> = Pixel | TDefault;

export type ImageAttributes = {
  src: string | null;
  width: PixelAttribute<"35%" | number>;
  height: PixelAttribute<"auto" | number>;
  aspectRatio: number | null;
  id: string | null;
};

type Size = {
  width: PixelAttribute<"35%">;
  height: PixelAttribute<"auto">;
  aspectRatio: number | null;
};

const ensurePixelString = <TDefault,>(value: Pixel | TDefault | number | undefined | null, defaultValue?: TDefault) => {
  if (!value || value === defaultValue) {
    return defaultValue;
  }

  if (typeof value === "number") {
    return `${value}px` satisfies Pixel;
  }

  return value;
};

type CustomImageBlockProps = CustomImageNodeViewProps & {
  imageFromFileSystem: string;
  setFailedToLoadImage: (_: boolean) => void;
  editorContainer: HTMLDivElement | null;
  setEditorContainer: (_: HTMLDivElement | null) => void;
};

export const CustomImageBlock: FC<CustomImageBlockProps> = (props) => {
  // props
  const { node, updateAttributes, setFailedToLoadImage, imageFromFileSystem, selected, getPos, editor } = props;
  const { src: remoteImageSrc, width, height, aspectRatio } = node.attrs;
  // states
  const [size, setSize] = useState<Size>({
    width: ensurePixelString(width, "35%") as PixelAttribute<"35%">,
    height: ensurePixelString(height, "auto") as PixelAttribute<"auto">,
    aspectRatio: aspectRatio || 1,
  });
  const [initialResizeComplete, setInitialResizeComplete] = useState(false);
  // refs
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const updateAttributesSafely = useCallback(
    (attributes: Partial<ImageAttributes>, errorMessage: string) => {
      try {
        updateAttributes(attributes);
      } catch (error) {
        console.error(`${errorMessage}:`, error);
      }
    },
    [updateAttributes]
  );

  const handleImageLoad = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;

    // add maximum dimension constraints
    const MAX_WIDTH = 800;
    const containerWidth = containerRef.current?.parentElement?.clientWidth || 800;
    const effectiveMaxWidth = Math.min(MAX_WIDTH, containerWidth);
    const aspectRatio = img.naturalWidth / img.naturalHeight;

    let computedWidth = img.naturalWidth;
    let computedHeight = img.naturalHeight;

    // scale down if image is too wide
    if (computedWidth > effectiveMaxWidth) {
      computedWidth = effectiveMaxWidth;
      computedHeight = computedWidth / aspectRatio;
    }

    const initialComputedSize = {
      width: `${computedWidth}px` satisfies Pixel,
      height: `${computedHeight}px` satisfies Pixel,
      aspectRatio: aspectRatio,
    };

    setSize(initialComputedSize);
    updateAttributesSafely(initialComputedSize, "Failed to update attributes while initializing image:");

    setInitialResizeComplete(true);
  }, [updateAttributes]);

  // for real time resizing
  useLayoutEffect(() => {
    setSize((prevSize) => ({
      ...prevSize,
      width: ensurePixelString(width, "35%") as PixelAttribute<"35%">,
      height: ensurePixelString(height, "auto") as PixelAttribute<"auto">,
    }));
  }, [width, height]);

  const handleImageMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const pos = getPos();
      const nodeSelection = NodeSelection.create(editor.state.doc, pos);
      editor.view.dispatch(editor.state.tr.setSelection(nodeSelection));
    },
    [editor, getPos]
  );

  // show the image loader if the remote image's src or preview image from filesystem is not set yet (while loading the image post upload) (or)
  // if the initial resize (from 35% width and "auto" height attrs to the actual size in px) is not complete
  const showImageLoader = !(remoteImageSrc || imageFromFileSystem) || !initialResizeComplete;
  // show the preview image from the file system if the remote image's src is not set
  const displayedImageSrc = remoteImageSrc ?? imageFromFileSystem;

  return (
    <div
      ref={containerRef}
      className="group/image-component relative inline-block max-w-full"
      onMouseDown={handleImageMouseDown}
      style={{
        width: size.width,
        aspectRatio: size.aspectRatio ?? undefined,
        maxWidth: "100%",
      }}
    >
      {showImageLoader && (
        <div className="animate-pulse bg-ash-900/10 rounded-md" style={{ width: size.width, height: size.height }} />
      )}
      <img
        alt={node.attrs.alt}
        ref={imageRef}
        src={displayedImageSrc}
        onLoad={handleImageLoad}
        onError={(e) => {
          console.error("Error loading image", e);
          setFailedToLoadImage(true);
        }}
        width={size.width}
        className={cn("image-component block rounded-md max-w-full h-auto object-contain", {
          hidden: showImageLoader,
          "read-only-image": !editor.isEditable,
          "blur-xs opacity-80": !remoteImageSrc,
        })}
        style={{
          width: size.width,
          maxWidth: "100%",
          aspectRatio: size.aspectRatio ?? undefined,
        }}
      />
      {selected && displayedImageSrc === remoteImageSrc && <div className="absolute inset-0 size-full bg-ash-900/30" />}
    </div>
  );
};
